import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * 排行榜预加载和缓存 Hook
 * 在用户登录后自动预加载日榜和总榜数据
 */
export function useLeaderboardCache() {
  const queryClient = useQueryClient()

  const preloadLeaderboard = async (period: 'today' | 'all') => {
    try {
      const queryKey = period === 'today'
        ? ['leaderboard', 'today', new Date().toISOString().split('T')[0]]
        : ['leaderboard', period]

      // 检查是否已有新鲜的缓存数据
      const queryState = queryClient.getQueryState(queryKey)
      const now = Date.now()
      const staleTime = 1000 * 60 * 3 // 3分钟

      if (queryState?.data && queryState.dataUpdatedAt && (now - queryState.dataUpdatedAt) < staleTime) {
        console.log(`📊 排行榜 ${period} 缓存仍然新鲜，跳过预加载`)
        return
      }

      console.log(`🔄 开始预加载排行榜 ${period}...`)

      // 预加载数据，使用更短的超时时间
      await queryClient.prefetchQuery({
        queryKey,
        queryFn: () => api.getLeaderboard(period, period === 'today' ? new Date().toISOString().split('T')[0] : undefined),
        staleTime: 1000 * 60 * 3, // 3分钟内不重新获取
        cacheTime: 1000 * 60 * 15, // 缓存15分钟
      })

      console.log(`✅ 排行榜 ${period} 预加载完成`)
    } catch (error) {
      console.warn(`⚠️ 排行榜 ${period} 预加载失败:`, error)
    }
  }

  const preloadAllLeaderboards = async () => {
    // 并行预加载日榜和总榜
    await Promise.allSettled([
      preloadLeaderboard('today'),
      preloadLeaderboard('all')
    ])
  }

  return {
    preloadLeaderboard,
    preloadAllLeaderboards
  }
}

/**
 * 自动预加载排行榜的 Hook
 * 在组件挂载时自动开始预加载
 */
export function useAutoPreloadLeaderboards() {
  const { preloadAllLeaderboards } = useLeaderboardCache()

  useEffect(() => {
    // 延迟1秒开始预加载，避免阻塞主要内容
    const timer = setTimeout(() => {
      preloadAllLeaderboards()
    }, 1000)

    return () => clearTimeout(timer)
  }, [preloadAllLeaderboards])
}
